import React, {
  createContext,
  forwardRef,
  type ForwardRefExoticComponent,
  type PropsWithoutRef,
} from "react";
import type { BaseProps } from "@purpur/common-types";
import c from "classnames/bind";

import styles from "./listbox.module.scss";
import { ListboxItem } from "./listbox-item";

const cx = c.bind(styles);

const rootClassName = "purpur-listbox";

export const ListboxNegativeContext = createContext<boolean | undefined>(undefined);

export type ListboxProps = Omit<BaseProps<"ul">, "role"> & {
  "aria-label": NonNullable<BaseProps<"ul">["aria-label"]>;
  "aria-expanded": NonNullable<BaseProps<"ul">["aria-expanded"]>;
  /** When true, renders the Listbox for use on dark or colored backgrounds. */
  negative?: boolean;
};

type ListboxStatic = {
  Item: typeof ListboxItem;
};

type ListboxComponent = ForwardRefExoticComponent<PropsWithoutRef<ListboxProps>> & ListboxStatic;

export const Listbox = forwardRef<HTMLUListElement, ListboxProps>(
  ({ children, negative = false, ...listboxProps }, ref) => (
    <ul
      {...listboxProps}
      ref={ref}
      className={cx(rootClassName, listboxProps.className, {
        [`${rootClassName}--negative`]: negative,
      })}
      role="listbox"
    >
      <ListboxNegativeContext.Provider value={negative}>{children}</ListboxNegativeContext.Provider>
    </ul>
  )
) as ListboxComponent;

export type { ListboxItemProps } from "./listbox-item";
Listbox.Item = ListboxItem;
Listbox.displayName = "ListBox";
